import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp, useT } from "@/context/AppContext";
import { usePartner } from "@/context/PartnerContext";
import { computeMonthly } from "@/lib/lease";
import { Search, GitCompare, Check } from "lucide-react";
import { motion } from "framer-motion";

export default function CatalogPage() {
  const t = useT();
  const { formatPrice, leaseSettings, compare, toggleCompare } = useApp();
  const { partner, prefix, isPartner } = usePartner();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [hardware, setHardware] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [f, setF] = useState({
    vendor: params.get("vendor") || "",
    sector: params.get("sector") || "",
    placement: params.get("placement") || "",
    type: "", q: "",
  });

  useEffect(() => {
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/sectors").then((r) => setSectors(r.data));
  }, []);

  useEffect(() => {
    const params = {};
    if (f.vendor) params.vendor = f.vendor;
    if (f.sector) params.sector = f.sector;
    if (f.placement) params.placement = f.placement;
    if (f.type) params.type = f.type;
    api.get("/hardware", { params }).then((r) => setHardware(r.data));
  }, [f.vendor, f.sector, f.placement, f.type]);

  const filtered = useMemo(() => {
    if (!f.q) return hardware;
    const q = f.q.toLowerCase();
    return hardware.filter((h) => h.name.toLowerCase().includes(q) || h.sku.toLowerCase().includes(q));
  }, [hardware, f.q]);

  return (
    <div className="container-x py-12" data-testid="catalog-page">
      <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742] mb-3">Catalogus</div>
      <h1 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">Alle kiosken</h1>
      <p className="text-[#A8B3AD] mt-2 max-w-xl">{(isPartner && partner?.show_vendor_names === false) ? "Filter op plaatsing en branche." : "Filter op leverancier, plaatsing en branche."}</p>

      <div className="grid lg:grid-cols-12 gap-8 mt-10">
        {/* Filters */}
        <aside className="lg:col-span-3 space-y-6">
          <div className="card-surface p-5">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 text-[#A8B3AD]" />
              <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })}
                className="input-field !p-0 !border-0 !bg-transparent" placeholder="Zoeken..."
                data-testid="catalog-search-input" />
            </div>
          </div>
          <FilterBlock title="Plaatsing">
            {[{ v: "", l: "Alle" }, { v: "indoor", l: "Binnen" }, { v: "outdoor", l: "Buiten" }].map((o) => (
              <button key={o.v} onClick={() => setF({ ...f, placement: o.v })}
                className={`chip w-full justify-start ${f.placement === o.v ? "!bg-[#1F3A2E] !text-white" : ""}`}
                data-testid={`filter-placement-${o.v || "all"}`}>{o.l}</button>
            ))}
          </FilterBlock>
          <FilterBlock title="Type">
            {[{v:"",l:"Alle"},{v:"counter",l:"Counter"},{v:"wall",l:"Wall"},{v:"freestanding",l:"Freestanding"},{v:"tablet",l:"Tablet"}].map((o) => (
              <button key={o.v} onClick={() => setF({ ...f, type: o.v })}
                className={`chip w-full justify-start ${f.type === o.v ? "!bg-[#1F3A2E] !text-white" : ""}`}
                data-testid={`filter-type-${o.v || "all"}`}>{o.l}</button>
            ))}
          </FilterBlock>
          {(!isPartner || partner?.show_vendor_names !== false) && (
            <FilterBlock title="Leverancier">
              <button onClick={() => setF({ ...f, vendor: "" })}
                className={`chip w-full justify-start ${!f.vendor ? "!bg-[#1F3A2E] !text-white" : ""}`}
                data-testid="filter-vendor-all">Alle</button>
              {vendors.map((v) => (
                <button key={v.slug} onClick={() => setF({ ...f, vendor: v.slug })}
                  className={`chip w-full justify-start ${f.vendor === v.slug ? "!bg-[#1F3A2E] !text-white" : ""}`}
                  data-testid={`filter-vendor-${v.slug}`}>{v.name}</button>
              ))}
            </FilterBlock>
          )}
          <FilterBlock title="Sector">
            <button onClick={() => setF({ ...f, sector: "" })}
              className={`chip w-full justify-start ${!f.sector ? "!bg-[#1F3A2E] !text-white" : ""}`}
              data-testid="filter-sector-all">Alle</button>
            {sectors.map((s) => (
              <button key={s.slug} onClick={() => setF({ ...f, sector: s.slug })}
                className={`chip w-full justify-start ${f.sector === s.slug ? "!bg-[#1F3A2E] !text-white" : ""}`}
                data-testid={`filter-sector-${s.slug}`}>{s.name_i18n?.nl || s.slug}</button>
            ))}
          </FilterBlock>
        </aside>

        {/* Grid */}
        <div className="lg:col-span-9">
          <div className="text-sm text-[#A8B3AD] mb-4">{filtered.length} producten</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((h, i) => (
              <motion.div key={h.id}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="card-interactive overflow-hidden flex flex-col cursor-pointer" onClick={() => nav(`${prefix}/product/${h.slug || h.id}`)}
                data-testid={`product-${h.id}-card`}
              >
                <div className="product-plate h-56 relative overflow-hidden">
                  {h.image_url && (
                    <img
                      src={h.image_url}
                      alt={h.name}
                      className="absolute inset-0 w-full h-full object-contain p-6"
                      loading="lazy"
                    />
                  )}
                  {/* Compare toggle (top-right) */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleCompare(h.id); }}
                    data-testid={`compare-toggle-${h.sku}`}
                    aria-label="Voeg toe aan vergelijken"
                    className={`absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider transition ${compare.includes(h.id) ? "bg-[#D97742] text-white" : "bg-black/40 backdrop-blur text-white hover:bg-black/60"}`}
                  >
                    {compare.includes(h.id) ? <Check className="w-3 h-3" /> : <GitCompare className="w-3 h-3" />}
                    {compare.includes(h.id) ? "Toegevoegd" : "Vergelijk"}
                  </button>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {(!isPartner || partner?.show_vendor_names !== false) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setF((prev) => ({ ...prev, vendor: prev.vendor === h.vendor_slug ? "" : h.vendor_slug })); }}
                        className="chip chip-outline hover:text-[#6FAA8A] hover:border-[#6FAA8A] transition"
                        data-testid={`product-${h.id}-vendor`}
                      >
                        {h.vendor_slug}
                      </button>
                    )}
                    <span className="chip chip-outline capitalize">{h.placement}</span>
                  </div>
                  <div className="font-semibold text-[#F4F1EA] hover:text-[#6FAA8A] transition leading-snug">
                    {(isPartner && partner?.show_vendor_names === false) ? (h.name || "").replace(/^(QIOX|Interact Kiosk|IK) /, "") : h.name}
                  </div>
                  <div className="mt-auto pt-3 flex items-baseline justify-between">
                    <div>
                      <div className="text-xs text-[#A8B3AD]">vanaf</div>
                      <div className="display text-lg font-semibold text-[#6FAA8A]">{formatPrice(computeMonthly(h.purchase_price || 0, leaseSettings).monthly)}/mnd</div>
                    </div>
                    <div className="text-xs text-[#A8B3AD]">
                      of koop {formatPrice(h.purchase_price)}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterBlock({ title, children }) {
  return (
    <div className="card-surface p-5">
      <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A] mb-3">{title}</div>
      <div className="flex flex-col gap-1.5">{children}</div>
    </div>
  );
}
